'use client';

// ---------------------------------------------------------------------------
// MusicProvider
// ---------------------------------------------------------------------------
//
// Mounted in providers.tsx, which is the ONLY node React never remounts. Each
// route group — (platform), (chain), (pms) — has its own layout, and crossing
// between them unmounts that layout along with its Topbar. So an <audio> element
// owned by any of them would stop the music the moment someone went from the
// dashboard to the admin area.
//
// It must sit INSIDE ToastProvider, AuthProvider and LocaleContext, because the
// components it mounts consume all three. `core/providers/provider-order.test.ts`
// parses providers.tsx and enforces that: it is the test that caught the same
// mistake for CallProvider, where reversing the order made every authenticated
// route return 500 during SSR.
//
// Deliberately thin. The provider supplies no context of its own — playback state
// lives in a module-level zustand store (modules/music/stores/music.store.ts),
// matching the calling feature. A context would add a re-render boundary for
// nothing, since the store is reachable from anywhere already.
// ---------------------------------------------------------------------------

import { MusicAudioEngine } from '@/modules/music/components/MusicAudioEngine';
import { MusicPlayerHost } from '@/modules/music/components/MusicPlayerHost';
import { MusicScheduleRunner } from '@/modules/music/components/MusicScheduleRunner';

export function MusicProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      {/* The audio element. Renders nothing visible and is never unmounted. */}
      <MusicAudioEngine />
      {/*
        The panels, rendered here rather than from the Topbar button.
        The Topbar unmounts when you cross route groups, so a panel anchored to
        it would vanish mid-track; the trigger there only toggles store state.
      */}
      <MusicPlayerHost />
      {/*
        The schedule clock. Here rather than inside the panel so a window fires
        whether or not anyone has opened the player — on a front desk, nobody
        may have. Inside the panel, closing it would cancel the armed wake-up.
      */}
      <MusicScheduleRunner />
    </>
  );
}
